import React, { useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { postWebUiApi } from '../api';
import WebUiPageWrapper from '../components/page-wrapper';

type InterfaceState = {
  interface: number;
  state: boolean;
  speed: string;
  duplex: boolean;
};

type DeviceId = { 'part number': string; poe?: boolean };
type Uptime = { uptime: string };
type Supply = { v1: boolean; v2: boolean };
type SysInfo = { name: string; description: string };
type PoeCfg = Array<{ limit_pwr: number }>;
type PoeState = Array<{ poeclass: number; status: number }>;
type PoeGlobal = { usedBudget: number };

const PoEClassW = [15.4, 4, 7, 15.4, 30];
const PoEUserPWRLimit = [30, 2, 4, 6, 8, 10, 12, 15, 20, 24, 18];
const PoEPortName = [
  'P2',
  'P3',
  'P4',
  'P5',
  'P6',
  'P7',
  'P8',
  'P9',
  'P12',
  'P13',
  'P14',
  'P15',
  'P16',
  'P17',
  'P18',
  'P19',
];

export default function WebUiDashboardPage() {
  const [iface, setIface] = useState<InterfaceState[]>([]);
  const [id, setId] = useState<DeviceId | null>(null);
  const [uptime, setUptime] = useState<string>('');
  const [sysName, setSysName] = useState('');
  const [sysDesc, setSysDesc] = useState('');
  const [pwr1, setPwr1] = useState('');
  const [pwr2, setPwr2] = useState('');
  const [poeBudget, setPoeBudget] = useState<string>('');
  const [poeCfg, setPoeCfg] = useState<PoeCfg>([]);
  const [poeState, setPoeState] = useState<PoeState>([]);

  useEffect(() => {
    let alive = true;
    const loadStatic = async () => {
      const [rid, up, info, sup] = await Promise.all([
        postWebUiApi<DeviceId>({
          service: 'system',
          access: 'get',
          command: 'get id',
        }),
        postWebUiApi<Uptime>({
          service: 'system',
          access: 'get',
          command: 'get uptime',
        }),
        postWebUiApi<SysInfo>({
          service: 'system',
          access: 'get',
          command: 'get info',
        }),
        postWebUiApi<Supply>({
          service: 'system',
          access: 'get',
          command: 'get supply',
        }),
      ]);
      if (!alive) return;
      setId(rid);
      setUptime(up.uptime);
      setSysName(info.name);
      setSysDesc(info.description);
      setPwr1(sup.v1 ? 'Present' : 'Not present');
      setPwr2(sup.v2 ? 'Present' : 'Not present');

      if (rid.poe) {
        const [cfg, global] = await Promise.all([
          postWebUiApi<PoeCfg>({
            service: 'poe',
            access: 'get',
            command: 'get port cfg state',
          }),
          postWebUiApi<PoeGlobal>({
            service: 'poe',
            access: 'get',
            command: 'get global state',
          }),
        ]);
        if (!alive) return;
        setPoeCfg(cfg);
        if (global.usedBudget < 0) setPoeBudget('PoE Failure');
        else setPoeBudget(`${global.usedBudget / 10}W`);
      }
    };

    const loadDynamic = async () => {
      const data = await postWebUiApi<InterfaceState[]>({
        service: 'interface',
        access: 'get',
        command: 'get interfaces state',
      });
      if (!alive) return;
      setIface(data);
      if (id?.poe) {
        const st = await postWebUiApi<PoeState>({
          service: 'poe',
          access: 'get',
          command: 'get poe state',
        });
        if (!alive) return;
        setPoeState(st);
      }
    };

    loadStatic().catch(() => {});
    loadDynamic().catch(() => {});
    const t = window.setInterval(() => loadDynamic().catch(() => {}), 1000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [id?.poe]);

  return (
    <WebUiPageWrapper titleList={['Dashboard']} contentClassName="flex-row">
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-row gap-4">
          <div className="w-full">
            <h4 className="font-bold">Quick Diagnostics</h4>
            <table className="webui-table w-full">
              <thead>
                <tr className="webui-table-row-head">
                  <th>Parameter</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="webui-table-row-content">
                  <td>Uptime</td>
                  <td className="truncate">{uptime}</td>
                </tr>
                <tr className="webui-table-row-content">
                  <td>Power supply V1</td>
                  <td className="truncate">{pwr1}</td>
                </tr>
                <tr className="webui-table-row-content">
                  <td>Power supply V2</td>
                  <td className="truncate">{pwr2}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="w-full">
            <h4 className="font-bold">Current Settings</h4>
            <table className="webui-table w-full">
              <thead>
                <tr className="webui-table-row-head">
                  <th>Parameter</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="webui-table-row-content">
                  <td>System Name</td>
                  <td className="truncate">{sysName}</td>
                </tr>
                <tr className="webui-table-row-content">
                  <td>System Description</td>
                  <td className="truncate">{sysDesc}</td>
                </tr>
                <tr className="webui-table-row-content">
                  <td>{id?.poe ? 'PoE budget allocated' : ''}</td>
                  <td className="truncate">{id?.poe ? poeBudget : ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex w-full flex-row gap-4">
          <div className="w-full">
            <h4 className="font-bold">Port Status</h4>
            <table className="webui-table w-full">
              <thead>
                <tr className="webui-table-row-head">
                  <th className="whitespace-nowrap text-center">Port</th>
                  <th className="whitespace-nowrap text-center">Link Status</th>
                  <th className="whitespace-nowrap text-center">Speed</th>
                  <th className="whitespace-nowrap text-center">Duplex</th>
                </tr>
              </thead>
              <tbody>
                {iface.map((p) => (
                  <tr key={p.interface} className="webui-table-row-content">
                    <td className="text-center">{p.interface}</td>
                    <td className="text-center">{p.state ? 'up' : 'down'}</td>
                    <td className="text-center">{p.state ? p.speed : '-'}</td>
                    <td className="text-center">
                      {p.state ? `${p.duplex ? 'full' : 'half'}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="w-full">
            {id?.poe ? (
              <div className="w-full">
                <h4 className="font-bold">PoE Status</h4>
                <table className="webui-table w-full table-fixed">
                  <thead>
                    <tr className="webui-table-row-head text-center">
                      <th className="text-center">Port</th>
                      <th className="text-center">PoE Status</th>
                      <th className="text-center">Allocated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PoEPortName.map((name, idx) => {
                      const st = poeState[idx];
                      const cfg = poeCfg[idx];
                      const poeclass = st?.poeclass ?? -1;
                      const allocated =
                        poeclass === -1
                          ? '-'
                          : `${Math.min(
                              PoEClassW[poeclass] ?? 0,
                              PoEUserPWRLimit[cfg?.limit_pwr ?? 0] ?? 0,
                            )}W`;
                      return (
                        <tr
                          key={name}
                          className={twMerge([
                            'webui-table-row-content',
                            poeclass === -1 && 'bg-webui-tertiary',
                          ])}
                        >
                          <td className="text-center">{name}</td>
                          <td className="text-center">
                            {st ? String(st.status) : '-'}
                          </td>
                          <td className="text-center">{allocated}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex w-full items-start justify-center">
        <img
          src={`/web-ui/${id?.['part number']}.png`}
          alt="Dashboard"
          className="h-auto w-full object-contain"
        />
      </div>
    </WebUiPageWrapper>
  );
}
